import {IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {EditLine} from '@dropbox/dig-icons/assets';
import {pulseUserAtom} from 'atoms/auth';
import {EditorDoc} from 'client';
import {
  DocumentDateCell,
  DocumentFacepilesCell,
  DocumentNameCell,
} from 'components/DSYS/document/DocumentsTable';
import {DocumentStatusChip} from 'components/DSYS/document/DocumentStatusChip';
import {canEditDocument, getDocStatus, getStatusIconSrc} from 'components/DSYS/document/utils';
import {Table} from 'components/DSYS/Table';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useCallback, useState} from 'react';
import {useNavigate} from 'react-router';

import {useDeletePlanningDoc} from './hooks';
import {PlanningCycleModal} from './PlanningCycleModal';
import {createPlanningDocUrl, formatCreatedDate, isHardcodedTemplate} from './utils';

export const planningDocColumns = [
  {
    type: 'document_title',
    minWidth: 300,
    skeleton: () => (
      <Stack gap="12">
        <Skeleton.Text width={200} />
        <Box display="flex" flexDirection="row" style={{gap: 8}} alignItems="center">
          <Skeleton.Avatar size="xsmall" />
          <Skeleton.Text width={60} />
        </Box>
      </Stack>
    ),
  },
  {
    type: 'created_on',
    minWidth: 250,
    width: 250,
    skeleton: () => (
      <Box>
        <Skeleton.Text width={90} />
      </Box>
    ),
  },
  {
    type: 'status',
    minWidth: 250,
    width: 250,
    skeleton: () => (
      <Box display="flex" flexDirection="row" style={{gap: 8}} alignItems="center">
        <Chip size="small">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Chip>
        <Skeleton.Text width={40} />
      </Box>
    ),
  },
  {
    type: 'collaborators',
    minWidth: 300,
    skeleton: () => (
      <Box>
        <Skeleton.Avatar />
      </Box>
    ),
  },
];

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
export const PlanningDocTableRow = ({
  doc,
  dbId,
  documentId,
  isPinned,
  onTogglePin,
  enablePlanningCycleModal,
  splitDateAndPlanningCycle,
  showYjsSize,
  yjsSize,
  documentBodyUpdatedAtIso,
}: {
  doc: EditorDoc;
  dbId?: number;
  documentId?: string | null;
  isPinned?: boolean;
  onTogglePin?: () => void;
  enablePlanningCycleModal?: boolean;
  /** When true (admin table), created date and planning cycle are separate columns for sorting. */
  splitDateAndPlanningCycle?: boolean;
  showYjsSize?: boolean;
  yjsSize?: number | null;
  /** Admin: ISO timestamp from `documents.updated_at`; `null` if no linked document. Omit for non-admin tables. */
  documentBodyUpdatedAtIso?: string | null;
}) => {
  const docName = doc.name || t('untitled');
  const docUrl = createPlanningDocUrl(doc, Boolean(dbId));
  const docOwnerLdap = doc.owner.ldap;
  const editorLdaps = doc.employees.map((employee) => employee.ldap);
  const documentStatus = getDocStatus(isHardcodedTemplate(doc.id) ? 'template' : doc.status);
  const updatedDate = doc.updated_at ? formatCreatedDate(doc.updated_at) : undefined;
  const planningCycle =
    doc.planning_cycle && doc.planning_year
      ? `${doc.planning_cycle} ${doc.planning_year}`
      : undefined;
  const pulseUser = useAtomValue(pulseUserAtom);
  const canEdit = canEditDocument(pulseUser, doc.owner.ldap);
  const navigate = useNavigate();

  const [isHovered, setIsHovered] = useState(false);
  const [showPlanningCycleModal, setShowPlanningCycleModal] = useState(false);
  const [isPlanningCycleHovered, setIsPlanningCycleHovered] = useState(false);

  const navigateToPlanningDoc = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      const target = e.target as HTMLElement;
      const isModalClick =
        target.classList.contains('dig-Modal-overlay') || target.closest('.dig-Modal-overlay');
      if (isModalClick) return;

      e.stopPropagation();
      navigate(docUrl);
    },
    [navigate, docUrl]
  );

  return (
    <>
      <Table.Row
        isSelectable
        onClick={canEdit ? undefined : navigateToPlanningDoc}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <DocumentNameCell
          docName={docName}
          docUrl={docUrl}
          docOwner={doc.owner}
          dbId={dbId}
          documentId={documentId}
          docNameMaxWidth={500}
          showOverflowMenu={canEdit && isHovered}
          deleteModalInfo={{
            documentId: doc.id,
            useDeleteDoc: useDeletePlanningDoc,
            modalTitle: t('delete_planning_doc_title'),
            modalSubtitle: t('delete_planning_doc_subtitle'),
            width: 'standard',
          }}
          onClick={navigateToPlanningDoc}
          isPinned={isPinned}
          onTogglePin={onTogglePin}
        />

        {splitDateAndPlanningCycle ? (
          <>
            <DocumentDateCell isoDate={doc.created_at} onClick={navigateToPlanningDoc} />
            {documentBodyUpdatedAtIso !== undefined ? (
              documentBodyUpdatedAtIso ? (
                <DocumentDateCell
                  isoDate={documentBodyUpdatedAtIso}
                  onClick={navigateToPlanningDoc}
                />
              ) : (
                <Table.Cell onClick={navigateToPlanningDoc}>
                  <Text color="faint">—</Text>
                </Table.Cell>
              )
            ) : null}
            <Table.Cell
              onClick={dbId && enablePlanningCycleModal ? undefined : navigateToPlanningDoc}
            >
              {dbId ? (
                enablePlanningCycleModal ? (
                  <Box
                    tagName="span"
                    display="flex"
                    alignItems="flex-start"
                    style={{gap: 8}}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      setShowPlanningCycleModal(true);
                    }}
                    onMouseEnter={() => setIsPlanningCycleHovered(true)}
                    onMouseLeave={() => setIsPlanningCycleHovered(false)}
                  >
                    <Text>{planningCycle ?? t('planning_cycle_not_set')}</Text>
                    {isPlanningCycleHovered && (
                      <IconButton
                        variant="transparent"
                        shape="circular"
                        style={{marginTop: '-4px'}}
                      >
                        <UIIcon src={EditLine} />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Text>{planningCycle ?? t('planning_cycle_not_set')}</Text>
                )
              ) : (
                <Text color="faint">-</Text>
              )}
            </Table.Cell>
          </>
        ) : (
          <DocumentDateCell
            title={dbId ? `${t('created_on')}: ` : undefined}
            isoDate={doc.created_at}
            onClick={navigateToPlanningDoc}
          >
            {dbId && enablePlanningCycleModal ? (
              <Box
                tagName="span"
                display="flex"
                alignItems="flex-start"
                marginTop="8"
                style={{gap: 8}}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setShowPlanningCycleModal(true);
                }}
                onMouseEnter={() => setIsPlanningCycleHovered(true)}
                onMouseLeave={() => setIsPlanningCycleHovered(false)}
              >
                <Text>
                  {t('planning_cycle')}: {planningCycle ?? t('planning_cycle_not_set')}
                </Text>
                {isPlanningCycleHovered && (
                  <IconButton variant="transparent" shape="circular" style={{marginTop: '-4px'}}>
                    <UIIcon src={EditLine} />
                  </IconButton>
                )}
              </Box>
            ) : undefined}
          </DocumentDateCell>
        )}

        <Table.Cell onClick={dbId ? undefined : navigateToPlanningDoc}>
          <Box display="flex" alignItems="center" style={{gap: 6}}>
            <DocumentStatusChip
              status={documentStatus}
              statusIconSrc={getStatusIconSrc(documentStatus)}
              style={{cursor: 'pointer'}}
              boldStatusText
            />
            {updatedDate && (
              <Text color="faint" size="small">
                {updatedDate}
              </Text>
            )}
          </Box>
        </Table.Cell>

        {showYjsSize && (
          <Table.Cell onClick={navigateToPlanningDoc}>
            <Text color={yjsSize != null ? undefined : 'faint'}>
              {yjsSize != null ? formatBytes(yjsSize) : '—'}
            </Text>
          </Table.Cell>
        )}

        <DocumentFacepilesCell
          docOwnerLdap={docOwnerLdap}
          ldaps={dbId ? [] : editorLdaps}
          onClick={navigateToPlanningDoc}
        />
      </Table.Row>

      {dbId && enablePlanningCycleModal && (
        <PlanningCycleModal
          open={showPlanningCycleModal}
          onClose={() => setShowPlanningCycleModal(false)}
          planningId={doc.id}
          planningCycle={doc.planning_cycle ?? undefined}
          planningYear={doc.planning_year ?? undefined}
        />
      )}
    </>
  );
};
